import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Download, Printer, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, downloadText, errMsg, fmtDate, inr, tokenHeaders } from "@/lib/api";
import { StatusBadge, RecruitmentDisclaimer } from "@/components/site/Notices";
import { summaryRows } from "@/components/apply/StepsB";

const toText = (a) => {
  const s = summaryRows(a);
  const block = (t, rows) => `${t}\n${"-".repeat(t.length)}\n${rows.map(([k, v]) => `${k}: ${v ?? ""}`).join("\n")}\n`;
  return [`BMI SP GROUP – Application Summary`, `Reference: ${a.reference}`, `Status: ${a.status}`, `Payment: ${a.payment_status}${a.receipt_no ? ` (receipt ${a.receipt_no})` : ""}`, "",
    block("Position", s.job), block("Personal", s.personal), block("Education", s.education), block("Documents", s.docs),
    "Receiving an application does not mean the applicant has been selected. Final recruitment depends on eligibility, verification, interviews and the company's genuine recruitment requirements."].join("\n");
};

export default function ApplicationView() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/applications/${id}`, { headers: tokenHeaders("application", id) }).then((r) => setA(r.data)).catch((e) => setErr(errMsg(e))), [id]);
  useEffect(() => {
    load();
  }, [load]);

  const refresh = async () => {
    setBusy(true);
    try {
      await api.post(`/applications/${id}/refresh-payment`, {}, { headers: tokenHeaders("application", id) });
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (err) return <div className="container-x py-24 text-center"><p data-testid="application-error">{err}</p><Link to="/my-applications" className="mt-4 inline-block underline">Find my applications</Link></div>;
  if (!a) return <div className="container-x py-24 text-center text-slate-500">Loading…</div>;
  const paid = a.payment_status === "paid";

  return (
    <div className="container-x max-w-4xl py-12">
      <div className={`no-print mb-6 flex items-start gap-3 rounded-2xl p-5 ${paid ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-900"}`} data-testid={paid ? "application-success-banner" : "application-failure-banner"}>
        {paid ? <CheckCircle2 className="h-6 w-6 shrink-0" /> : <XCircle className="h-6 w-6 shrink-0" />}
        <div>
          <p className="font-display text-lg font-bold">{paid ? "Application submitted — payment verified" : "Application not submitted yet"}</p>
          <p className="text-sm">{paid ? "Your application has been received. Receiving an application does not mean you have been selected." : `The ${inr(a.fee_amount)} fee has not been verified. If money was debited, tap “Check payment status” — we confirm directly with Razorpay.`}</p>
        </div>
      </div>
      {!paid && (
        <div className="no-print mb-6 flex flex-wrap gap-3">
          <Link to={`/apply/${a.id}?step=6`} className="rounded-full bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-700" data-testid="application-retry-payment">Retry payment</Link>
          <Button variant="outline" className="rounded-full" disabled={busy} onClick={refresh} data-testid="application-check-status"><RefreshCw className="h-4 w-4" /> Check payment status</Button>
        </div>
      )}
      <div className="print-area rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10" data-testid="application-receipt">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 pb-6">
          <div><p className="font-display text-xl font-extrabold">BMI SP <span className="text-amber-600">GROUP</span></p><p className="text-xs text-slate-500">Application {paid ? "receipt" : "summary"}</p></div>
          <div className="text-right text-sm"><p className="font-mono-num text-lg font-bold" data-testid="application-reference">{a.reference}</p><StatusBadge status={a.status} testid="application-status" /></div>
        </div>
        <dl className="grid gap-4 py-6 text-sm sm:grid-cols-2">
          {[["Applicant", a.personal.full_name], ["Position applied for", a.job_title], ["Submitted on", fmtDate(a.submitted_at, true)], ["Payment status", paid ? "Verified ✓" : a.payment_status],
            ["Application fee", inr(a.fee_amount)], ["Receipt number", a.receipt_no], ["Razorpay payment ID", a.payment?.razorpay_payment_id], ["Razorpay order ID", a.payment?.razorpay_order_id]].map(([k, v]) => (
            <div key={k}><dt className="text-xs uppercase tracking-widest text-slate-500">{k}</dt><dd className="font-semibold break-all" data-testid={`receipt-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v || "—"}</dd></div>
          ))}
        </dl>
        <p className="rounded-xl bg-slate-50 p-4 text-xs leading-relaxed text-slate-600">The {inr(a.fee_amount)} is an application processing/submission fee. It is not a payment for a job and does not guarantee employment, an interview or selection. Receiving an application does not mean the applicant has been selected.</p>
      </div>
      <div className="no-print mt-6 flex flex-wrap gap-3">
        <Button variant="outline" className="rounded-full" onClick={() => window.print()} data-testid="application-print-button"><Printer className="h-4 w-4" /> Print / Save receipt</Button>
        <Button variant="outline" className="rounded-full" onClick={() => downloadText(`${a.reference}-summary.txt`, toText(a))} data-testid="application-download-summary"><Download className="h-4 w-4" /> Download summary</Button>
      </div>
      <RecruitmentDisclaimer className="no-print mt-8" />
    </div>
  );
}
